import { forwardRef, type ButtonHTMLAttributes } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../lib/cn.js';

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: LucideIcon;
  /** Required accessible name. */
  label: string;
  /** Bordered square (default) or borderless. */
  variant?: 'tertiary' | 'ghost';
  /** 28px (default) or 24px square. */
  size?: 'md' | 'sm';
  /** Marks a toggle button state. */
  pressed?: boolean;
}

/** Square icon-only button. Always carries an aria-label; hover and focus both reveal it. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon: Icon, label, variant = 'tertiary', size = 'md', pressed, className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        size === 'md' ? 'h-7 w-7' : 'h-6 w-6',
        variant === 'tertiary' ? 'border border-line bg-panel text-ink hover:bg-card' : 'text-mute hover:bg-card hover:text-ink',
        pressed ? 'bg-card text-ink' : '',
        className,
      )}
      {...rest}
    >
      <Icon size={size === 'md' ? 13 : 12} strokeWidth={1.5} aria-hidden />
    </button>
  );
});
